import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';

interface Blocked {
  id: number;
  username: string;
  display_name: string | null;
  avatar?: string | null;
}

/**
 * Who you have blocked, and the way back.
 *
 * ## Why this screen has to exist
 *
 * A block made from somebody's profile is undone from that same profile - which
 * works right up until you cannot remember the name, which is the normal case
 * some weeks later. Without a list, blocking is a decision you can make and not
 * unmake, and people hesitate over decisions they cannot reverse.
 *
 * It lists only your own blocks. There is deliberately no way to see who has
 * blocked you: that is the one fact a block is meant to keep quiet, and a
 * screen that leaked it would turn a private decision into an argument.
 */
export function BlockedAccounts() {
  const client = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['blocks'],
    queryFn: () => api.get<{ items: Blocked[] }>('/me/blocks'),
  });

  const unblock = useMutation({
    mutationFn: (username: string) => api.delete(`/users/${username}/block`),
    onSuccess: (_result, username) => {
      void client.invalidateQueries({ queryKey: ['blocks'] });
      // The profile carries `is_blocked`, so it is stale the moment this runs.
      void client.invalidateQueries({ queryKey: ['profile', username] });
    },
  });

  const items = data?.items ?? [];

  return (
    <Card className="p-6">
      <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">
        Blocked accounts
      </h2>

      <p className="mb-4 text-sm text-soft">
        They cannot message you, follow you, or comment on your tracks — and you
        will not see theirs. They are not told.
      </p>

      {isPending && <p className="font-mono text-[10px] text-ghost">loading…</p>}

      {!isPending && items.length === 0 && (
        <p className="text-sm text-faint">You have not blocked anybody.</p>
      )}

      <ul className="flex flex-col gap-1" data-blocked-list>
        {items.map((person) => (
          <li
            key={person.id}
            data-blocked={person.username}
            className="flex items-center gap-3 rounded-[var(--radius-control)] px-2 py-2"
          >
            <Avatar src={person.avatar ?? null} name={person.display_name ?? person.username} size={36} />

            <span className="min-w-0 flex-1">
              <Link
                to={`/${person.username}`}
                className="block truncate text-sm font-medium text-ink hover:underline"
              >
                {person.display_name ?? person.username}
              </Link>
              <span className="meta block truncate text-faint">@{person.username}</span>
            </span>

            <Button
              variant="secondary"
              loading={unblock.isPending && unblock.variables === person.username}
              onClick={() => unblock.mutate(person.username)}
              data-unblock={person.username}
            >
              Unblock
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
